import React from 'react'
import { Avatar, Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material"
import { DeckType } from "model/DeckType"
import { Duel, Game, GamePlayer, getOpponent, getUser, isWinner, isLoser } from "model/Game"
import { User } from "model/User"
import { groupBy } from "script/arrayutil"
import { useMediaContext } from 'context/MediaContext'
import { ArrowBackIosNew } from '@mui/icons-material'

type Props = {
  user: User
  games: Game[]
  backToTop: () => void
}
export default function BattleRecord(props: Props) {
  const { isPc } = useMediaContext()

  type Result = {
    id: string
    user: GamePlayer
    opponent: GamePlayer
    isWin: boolean
    isLose: boolean
    duels?: Duel[]
  }
  const results = props.games
    .map(it => {
      return {
        id: it.id,
        user: getUser(props.user, it),
        opponent: getOpponent(props.user, it),
        isWin: isWinner(props.user, it),
        isLose: isLoser(props.user, it),
        duels: it.duels
      }
    })
  const resultsByOpponentDeckType = groupBy(results, it => it.opponent.deck_type.id)
      .sort((a, b) => b[1].length - a[1].length)

  function getDeckTypeName(deckType: DeckType): string {
    if (!deckType) return ''
    const mainThemeName = deckType.main_theme.name
    const subThemeName = deckType.sub_theme?.name
    let name = mainThemeName
    if (subThemeName) name += ` + ${subThemeName}`
    return name
  }

  function getWinCount(results: Result[]): number {
    return results.filter(it => it.isWin).length
  }

  function getLoseCount(results: Result[]): number {
    return results.filter(it => it.isLose).length
  }

  function getWinRate(results: Result[]): number {
    const winrate = (
      getWinCount(results) / (getWinCount(results) + getLoseCount(results))
    ) * 100
    return Math.floor(winrate * Math.pow(10, 2)) / Math.pow(10, 2)
  }

  function getFirstWinCount(results: Result[]): number {
    return results
      .filter(it => it.isWin)
      .flatMap(it => {
        return it.duels!.filter(duel => duel.is_first)
      }).length
  }

  function getFirstLoseCount(results: Result[]): number {
    return results
      .filter(it => it.isLose)
      .flatMap(it => {
        return it.duels!.filter(duel => !duel.is_first)
      }).length
  }

  function getFirstWinRate(results: Result[]): number {
    const cnt = getFirstWinCount(results)
    const allcnt = cnt + getFirstLoseCount(results)
    const winrate = cnt/allcnt*100
    return Math.floor(winrate * Math.pow(10, 2)) / Math.pow(10, 2)
  }

  function getSecondWinCount(results: Result[]): number {
    return results
      .filter(it => it.isWin)
      .flatMap(it => {
        return it.duels!.filter(duel => !duel.is_first)
      }).length
  }

  function getSecondLoseCount(results: Result[]): number {
    return results
      .filter(it => it.isLose)
      .flatMap(it => {
        return it.duels!.filter(duel => duel.is_first)
      }).length
  }

  function getSecondWinRate(results: Result[]): number {
    const cnt = getSecondWinCount(results)
    const allcnt = cnt + getSecondLoseCount(results)
    const winrate = cnt/allcnt*100
    return Math.floor(winrate * Math.pow(10, 2)) / Math.pow(10, 2)
  }

  function fixWinRate(rate: number): string {
    if (isNaN(rate)) return ''
    else return `${rate}%`
  }
  
  return (
    <>
      <Button
        startIcon={<ArrowBackIosNew />}
        onClick={() => props.backToTop()}
      >
        戻る
      </Button>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
        p: 1,
        mb: 3,
      }}>
        <Box sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          mb: 1
        }}>
          <Avatar
            src={results[0]?.user.profile_image_url}
            sx={{height: 30, width: 30}}
          />
          <Typography color='text.secondary' variant='body2'>{results[0]?.user.name} の戦績</Typography>
        </Box>

        <Box sx={{
          display: 'flex',
          flexDirection: 'row',
        }}>
          <Avatar
            src={results[0]?.user.deck_type.thumbnail_url}
            sx={{height: 75, width: 75}}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', ml: 1 }} justifyContent='space-between'>
            <Typography sx={{mt: 0.5}}>{getDeckTypeName(results[0]?.user.deck_type)}</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
              <Box 
                sx={{display: 'flex', flexDirection: 'column'}} 
                textAlign='center' 
              >
                <Typography variant='body2' color='text.secondary'>勝ち</Typography>
                <Typography variant='body2'>{getWinCount(results)}</Typography>
              </Box>
              <Box 
                sx={{ml: 3, display: 'flex', flexDirection: 'column'}} 
                textAlign='center' 
              >
                <Typography variant='body2' color='text.secondary'>負け</Typography>
                <Typography variant='body2'>{getLoseCount(results)}</Typography>
              </Box>
              <Box 
                sx={{ml: 3, display: 'flex', flexDirection: 'column'}} 
                textAlign='center' 
              >
                <Typography variant='body2' color='text.secondary'>勝率</Typography>
                <Typography variant='body2'>{getWinRate(results)}%</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <TableContainer sx={{bgcolor: 'background.default'}}>
        <Table size={isPc ? 'medium' : 'small'}>
          <TableHead>
            <TableRow>
              <TableCell colSpan={2}>VS</TableCell>
              <TableCell>{isPc ? '勝ち' : '勝'}</TableCell>
              <TableCell>{isPc ? '負け' : '負'}</TableCell>
              <TableCell>勝率</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {resultsByOpponentDeckType.map(([_, _results]) =>
              <React.Fragment key={_results[0].id}>
                <TableRow>
                  <TableCell rowSpan={3}>
                    <Box sx={{display: 'flex', flexDirection: 'row'}} alignItems='center'>
                      <Avatar
                        src={_results[0].opponent.deck_type.thumbnail_url}
                        variant='rounded'
                        sx={{mr: 1}}
                      />
                      {getDeckTypeName(_results[0].opponent.deck_type)}
                    </Box>
                  </TableCell>
                  <TableCell>{isPc ? 'マッチ' : 'マ'}</TableCell>
                  <TableCell>{getWinCount(_results)}</TableCell>
                  <TableCell>{getLoseCount(_results)}</TableCell>
                  <TableCell>{fixWinRate(getWinRate(_results))}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{isPc ? '先攻' : '先'}</TableCell>
                  <TableCell>{getFirstWinCount(_results)}</TableCell>
                  <TableCell>{getFirstLoseCount(_results)}</TableCell>
                  <TableCell>{fixWinRate(getFirstWinRate(_results))}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{isPc ? '後攻' : '後'}</TableCell>
                  <TableCell>{getSecondWinCount(_results)}</TableCell>
                  <TableCell>{getSecondLoseCount(_results)}</TableCell>
                  <TableCell>{fixWinRate(getSecondWinRate(_results))}</TableCell>
                </TableRow>
              </React.Fragment>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  )
}